import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineSearch,
  HiOutlineClock,
  HiOutlineUpload,
  HiOutlineDownload,
} from "react-icons/hi";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import { useDashboardHome } from "hooks/useDashboardHome";
import { ActionCard, StatCard } from "views/dashboard/dashboardHomeShared";
import {
  exportStatusTableExcel,
  getStatusTable,
  type StatusTableRow,
  type StatusTableSortBy,
  type StatusTableSortOrder,
} from "api/statusExcelUpload";
import { resolveStatusMarkPath } from "utils/statusMarkRouting";
import { UploadStatusModal } from "components/UploadStatusModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

import statTrend from "assets/svg/dashboard-stat-trend.svg";
import statDocument from "assets/svg/dashboard-stat-document.svg";
import statChart from "assets/svg/dashboard-stat-chart.svg";
import actionReports from "assets/svg/dashboard-action-reports.svg";
import iconSearch from "assets/svg/icon-search.svg";
import iconCheckbox from "assets/svg/icon-checkbox.svg";
import iconMore from "assets/svg/icon-more.svg";

const PAGE_SIZE = 25;

const FILTER_ALL = "__all__";

const MARK_TYPES = ["Word", "Label", "Logo", "Sound", "Colour", "3D"] as const;

const STATUS_FILTER_OPTIONS = [
  "TM No. Allotted",
  "Formalities Check Pass",
  "Formalities Check Fail",
  "Marked for Exam",
  "Accepted",
  "Objected",
  "Examination Report - Accepted after Objection",
  "Examination Report - Hearing",
  "Advertised",
  "Advertised in Journal - Opposed",
  "Opposition Hearing",
  "Advertised in Journal - Not Opposed",
  "Registered",
  "Abandoned",
  "Refused",
] as const;

const CLASS_OPTIONS = Array.from({ length: 45 }, (_, i) => String(i + 1));

/** Sorted unique page numbers with gaps where ellipsis should appear. */
function buildPageList(current: number, total: number): (number | "gap")[] {
  if (total <= 1) return [1];
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set<number>();
  pages.add(1);
  pages.add(total);
  for (let i = current - 1; i <= current + 1; i++) {
    if (i >= 1 && i <= total) pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const out: (number | "gap")[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev > 0 && p - prev > 1) out.push("gap");
    out.push(p);
    prev = p;
  }
  return out;
}

function formatLastUpdatedLine(d: Date): string {
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function SortableTh({
  sortKey,
  sortBy,
  sortOrder,
  onSort,
  className,
  children,
}: {
  sortKey: StatusTableSortBy;
  sortBy: StatusTableSortBy;
  sortOrder: StatusTableSortOrder;
  onSort: (key: StatusTableSortBy) => void;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const active = sortBy === sortKey;
  return (
    <th
      className={`sticky top-0 z-10 bg-[#f7f7f7] px-4 py-2.5 text-left text-xs font-medium text-[#222] ${
        className ?? ""
      }`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex max-w-full items-center gap-1 rounded text-left hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-[#222]"
        aria-label={`Sort by ${String(sortKey)}`}
      >
        <span>{children}</span>
        {active ? (
          <span className="shrink-0 tabular-nums text-[#6d6d6d]" aria-hidden>
            {sortOrder === "ASC" ? "▲" : "▼"}
          </span>
        ) : null}
      </button>
    </th>
  );
}

export const DashboardUpdateStatusView = (): JSX.Element => {
  const navigate = useNavigate();
  const [trackQuery, setTrackQuery] = useState("");
  const [tableSearchQuery, setTableSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<StatusTableRow[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [navigatingId, setNavigatingId] = useState<string | null>(null);
  const [lastListFetchAt, setLastListFetchAt] = useState(() => new Date());
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<StatusTableSortBy>("tmNo");
  const [sortOrder, setSortOrder] = useState<StatusTableSortOrder>("ASC");
  const [filterClass, setFilterClass] = useState(FILTER_ALL);
  const [filterType, setFilterType] = useState(FILTER_ALL);
  const [filterStatus, setFilterStatus] = useState(FILTER_ALL);
  const [exportLoading, setExportLoading] = useState(false);

  const {
    stats,
    statsLoading,
    statsError,
    trackLoading,
    trackError,
    handleTrackRecord,
  } = useDashboardHome();

  const totalApplications = stats?.totalApplications ?? 0;
  const activeApplications = stats?.activeApplications ?? 0;
  const discussion = stats?.discussion ?? 0;
  const filing = stats?.filing ?? 0;
  const registeredTms = stats?.registeredTms ?? 0;
  const pendingItemsBadge = discussion + filing;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(tableSearchQuery), 300);
    return () => clearTimeout(t);
  }, [tableSearchQuery]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    filterClass,
    filterType,
    filterStatus,
    sortBy,
    sortOrder,
  ]);

  const pageList = useMemo(
    () => buildPageList(page, totalPages),
    [page, totalPages]
  );

  const handleSortColumn = (key: StatusTableSortBy): void => {
    if (key === sortBy) {
      setSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(key);
      setSortOrder("ASC");
    }
  };

  const clearColumnFilters = (): void => {
    setFilterClass(FILTER_ALL);
    setFilterType(FILTER_ALL);
    setFilterStatus(FILTER_ALL);
  };

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getStatusTable({
        page,
        limit: PAGE_SIZE,
        sortBy,
        sortOrder,
        class: filterClass !== FILTER_ALL ? Number(filterClass) : undefined,
        typeOfMark: filterType !== FILTER_ALL ? filterType : undefined,
        status: filterStatus !== FILTER_ALL ? filterStatus : undefined,
      });
      setItems(data.rows);
      setTotalRecords(data.total);
      setLastListFetchAt(new Date());
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load status table"
      );
      setItems([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortOrder, filterClass, filterType, filterStatus]);

  const handleExportExcel = async (): Promise<void> => {
    setExportLoading(true);
    try {
      await exportStatusTableExcel({
        sortBy,
        sortOrder,
        class: filterClass !== FILTER_ALL ? Number(filterClass) : undefined,
        typeOfMark: filterType !== FILTER_ALL ? filterType : undefined,
        status: filterStatus !== FILTER_ALL ? filterStatus : undefined,
      });
      toast.success("Excel download started");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    void fetchApplications();
  }, [fetchApplications]);

  const displayedRows = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (r) =>
        (r.mark ?? "").toLowerCase().includes(q) ||
        (r.tmNo ?? "").toLowerCase().includes(q) ||
        (r.status ?? "").toLowerCase().includes(q) ||
        (r.typeOfMark ?? "").toLowerCase().includes(q) ||
        String(r.classNumber ?? "").includes(q) ||
        (r.acknowledgementNo ?? "").toLowerCase().includes(q)
    );
  }, [items, debouncedSearch]);

  const toggleSelectAll = (): void => {
    if (selectedIds.size === displayedRows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedRows.map((row) => row.id)));
    }
  };

  const toggleSelectRow = (e: React.MouseEvent, id: string): void => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRowClick = async (row: StatusTableRow): Promise<void> => {
    setNavigatingId(row.id);
    try {
      const path = await resolveStatusMarkPath(row.leadId, {
        applicationId: row.id,
        tmNo: row.tmNo,
        mark: row.mark,
        typeOfMark: row.typeOfMark || null,
        classNumber:
          typeof row.classNumber === "number" ? row.classNumber : null,
        status: row.status,
      });
      navigate(path);
    } finally {
      setNavigatingId(null);
    }
  };

  const handleSubmitTrack = async (): Promise<void> => {
    const result = await handleTrackRecord(trackQuery);
    if (result !== null) {
      navigate("/dashboard/home/track-results", {
        state: { query: trackQuery.trim(), result },
      });
    }
  };

  return (
    <DashboardLayout headerBrand="UpRight" headerTitle="Dashboard">
      <main className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-5 md:px-6">
        <div className="mx-auto min-h-0 w-full max-w-[1404px] rounded-lg bg-white p-6">
          <div className="flex w-full flex-col items-start gap-[22px]">
            <div className="flex w-full shrink-0 flex-col items-start gap-1">
              <h1 className="whitespace-nowrap text-2xl font-bold leading-9 text-[#333]">
                Trademark Management Suite
              </h1>
              <p className="max-w-[341px] text-sm font-normal leading-5 text-[#777]">
                Enterprise-grade trademark processing platform
              </p>
            </div>

            {statsError && (
              <p className="text-xs text-[#fb2c36]">{statsError}</p>
            )}

            <div className="flex w-full flex-col items-stretch gap-5 xl:flex-row">
              <StatCard
                title="Total Applications"
                value={statsLoading ? "—" : totalApplications}
                subtitle="+3 this month"
                icon={statTrend}
                iconAlt="Trend"
              />
              <StatCard
                title="Active Applications"
                value={statsLoading ? "—" : activeApplications}
                subtitle="+3 this month"
                icon={statTrend}
                iconAlt="Trend"
              />
              <StatCard
                title="Discussion"
                value={statsLoading ? "—" : discussion}
                subtitle="Awaiting action"
                icon={statDocument}
                iconAlt="Discussion"
              />
              <StatCard
                title="Filling"
                value={statsLoading ? "—" : filing}
                subtitle="Awaiting action"
                icon={statDocument}
                iconAlt="Filling"
              />
              <StatCard
                title="Registered TMs"
                value={statsLoading ? "—" : registeredTms}
                subtitle="Total portfolio"
                icon={statChart}
                iconAlt="Registered"
              />
            </div>

            <div className="flex w-full flex-col items-stretch gap-5 lg:flex-row">
              <ActionCard
                iconSlot={
                  <div className="flex size-12 items-center justify-center rounded-[10px] bg-[#314158]">
                    <HiOutlineSearch
                      className="h-6 w-6 shrink-0 text-white"
                      strokeWidth={1.5}
                    />
                  </div>
                }
                title="Track Status"
                subtitle="Monitor Application progress"
                variant="light"
                onClick={() => navigate("/dashboard/home")}
              />
              <ActionCard
                iconSlot={
                  <div className="flex size-12 items-center justify-center rounded-[10px] bg-[#314158]">
                    <HiOutlineClock
                      className="h-6 w-6 shrink-0 text-white"
                      strokeWidth={1.5}
                    />
                  </div>
                }
                title="Pending Items"
                subtitle="Review pending actions"
                variant="light"
                badge={statsLoading ? undefined : pendingItemsBadge}
                onClick={() => navigate("/dashboard/home/pending-items")}
              />
              <ActionCard
                icon={actionReports}
                iconAlt="Generate reports"
                title="Generate Reports"
                subtitle="Analytics and insights"
                variant="light"
              />
              <ActionCard
                iconSlot={
                  <div className="flex size-12 items-center justify-center rounded-[10px] bg-[#4e4e4e]">
                    <HiOutlineUpload
                      className="h-6 w-6 shrink-0 text-white"
                      strokeWidth={1.5}
                    />
                  </div>
                }
                title="Update Status"
                subtitle="Update Status"
                variant="dark"
                className="border border-[#eee]"
              />
            </div>

            <div className="flex w-full max-w-full flex-col items-start gap-1">
              <label
                className="w-full text-xs font-normal leading-5 text-[#222]"
                htmlFor="update-status-track-input"
              >
                Mark or Application Number
              </label>
              <div className="flex w-full items-center rounded-md border border-[#eee] bg-white px-[10px] py-2">
                <input
                  id="update-status-track-input"
                  type="text"
                  className="bg-transparent w-full flex-1 text-xs font-normal leading-5 text-[#222] outline-none placeholder:text-[#6d6d6d]"
                  value={trackQuery}
                  onChange={(e) => setTrackQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleSubmitTrack();
                  }}
                  placeholder="Text Here"
                />
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => void handleSubmitTrack()}
                disabled={trackLoading || !trackQuery.trim()}
                className="inline-flex h-12 min-h-12 w-full items-center justify-center rounded-lg bg-[#222] px-4 text-center text-sm font-medium leading-5 text-white transition-colors hover:bg-[#333] disabled:cursor-not-allowed disabled:opacity-50 sm:w-[251px] sm:px-6 sm:text-base sm:leading-6"
              >
                {trackLoading ? "Searching…" : "Track Record"}
              </button>
            </div>

            {trackError && (
              <p className="text-xs text-[#fb2c36]">{trackError}</p>
            )}

            <div className="flex w-full flex-col items-start gap-6 rounded-lg border border-[#eee] bg-white p-5">
              <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
                <div className="flex min-w-0 shrink-0 flex-col gap-1">
                  <h2 className="text-xl font-semibold leading-7 text-[#222]">
                    Update Status
                  </h2>
                  <p className="text-xs font-medium leading-4 text-[#90a1b9]">
                    Last Updated on {formatLastUpdatedLine(lastListFetchAt)}
                  </p>
                </div>
                <div className="flex flex-row flex-wrap items-center gap-3 lg:justify-end">
                  <div className="bg-transparent flex h-9 w-full max-w-full shrink-0 flex-row items-center gap-2 rounded-md border border-[#eee] px-3 py-2 sm:w-[250px]">
                    <img
                      src={iconSearch}
                      alt=""
                      className="h-3.5 w-3.5 shrink-0"
                    />
                    <input
                      type="text"
                      placeholder="Search (current page)"
                      value={tableSearchQuery}
                      onChange={(e) => setTableSearchQuery(e.target.value)}
                      className="bg-transparent min-w-0 flex-1 border-0 text-xs font-normal leading-5 text-[#777] [background-color:transparent] placeholder:text-[#777] focus:outline-none"
                      aria-label="Filter rows on current page"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={exportLoading}
                    onClick={() => void handleExportExcel()}
                    className="flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md border border-[#eee] bg-white px-4 py-2 transition-colors hover:bg-[#f7f7f7] disabled:opacity-50"
                  >
                    <HiOutlineDownload
                      className="h-4 w-4 shrink-0 text-[#222]"
                      strokeWidth={2}
                    />
                    <span className="text-xs font-semibold leading-5 text-[#222]">
                      {exportLoading ? "Exporting…" : "Export Excel"}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadModalOpen(true)}
                    className="flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md bg-[#222] px-4 py-2 transition-colors hover:bg-[#333]"
                  >
                    <HiOutlineUpload
                      className="h-4 w-4 shrink-0 text-white"
                      strokeWidth={2}
                    />
                    <span className="text-xs font-semibold leading-5 text-white">
                      Upload New
                    </span>
                  </button>
                </div>
              </div>

              <div className="flex w-full flex-col gap-2 rounded-md border border-[#eee] bg-[#fafafa] px-3 py-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-4">
                <div className="flex min-w-0 flex-col gap-1 sm:w-[140px]">
                  <span className="text-xs font-medium text-[#222]">Class</span>
                  <Select value={filterClass} onValueChange={setFilterClass}>
                    <SelectTrigger className="h-9 w-full bg-white">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FILTER_ALL}>All classes</SelectItem>
                      {CLASS_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex min-w-0 flex-col gap-1 sm:w-[160px]">
                  <span className="text-xs font-medium text-[#222]">Type</span>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="h-9 w-full bg-white">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FILTER_ALL}>All types</SelectItem>
                      {MARK_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex min-w-0 flex-col gap-1 sm:min-w-[220px] sm:flex-1">
                  <span className="text-xs font-medium text-[#222]">
                    Status
                  </span>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="h-9 w-full bg-white">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[min(280px,50vh)]">
                      <SelectItem value={FILTER_ALL}>All statuses</SelectItem>
                      {STATUS_FILTER_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  onClick={clearColumnFilters}
                  className="h-9 rounded-md border border-[#eee] bg-white px-3 text-xs font-medium text-[#222] transition-colors hover:bg-[#f0f0f0] sm:mb-0"
                >
                  Clear filters
                </button>
              </div>

              <div className="flex min-h-0 w-full flex-col overflow-hidden rounded-md border border-[#eee]">
                <div className="max-h-[min(70vh,680px)] overflow-x-auto overflow-y-auto">
                  <table className="w-full min-w-[1100px] border-collapse">
                    <thead>
                      <tr className="border-b border-[#eee] bg-[#f7f7f7]">
                        <th className="sticky left-0 top-0 z-10 w-11 border-r border-[#eee] bg-[#f7f7f7] px-4 py-2.5 text-left">
                          <button
                            type="button"
                            onClick={toggleSelectAll}
                            disabled={loading || displayedRows.length === 0}
                            className="flex h-6 w-6 items-center justify-center disabled:opacity-50"
                            aria-label="Select all rows"
                          >
                            <img
                              src={iconCheckbox}
                              alt=""
                              className="h-6 w-6"
                            />
                          </button>
                        </th>
                        <th className="sticky top-0 z-10 w-14 bg-[#f7f7f7] px-4 py-2.5 text-left text-xs font-medium text-[#222]">
                          No.
                        </th>
                        <SortableTh
                          sortKey="mark"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSortColumn}
                          className="min-w-[100px]"
                        >
                          Mark
                        </SortableTh>
                        <SortableTh
                          sortKey="typeOfMark"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSortColumn}
                          className="min-w-[72px]"
                        >
                          Type
                        </SortableTh>
                        <SortableTh
                          sortKey="classNumber"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSortColumn}
                          className="w-16"
                        >
                          Class
                        </SortableTh>
                        <SortableTh
                          sortKey="tmNo"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSortColumn}
                          className="min-w-[104px]"
                        >
                          TM No.
                        </SortableTh>
                        <SortableTh
                          sortKey="applDate"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSortColumn}
                          className="min-w-[96px]"
                        >
                          Appl. date
                        </SortableTh>
                        <th className="sticky top-0 z-10 min-w-[112px] bg-[#f7f7f7] px-4 py-2.5 text-left text-xs font-medium text-[#222]">
                          Ack. no.
                        </th>
                        <th className="sticky top-0 z-10 min-w-[96px] bg-[#f7f7f7] px-4 py-2.5 text-left text-xs font-medium text-[#222]">
                          Filing date
                        </th>
                        <SortableTh
                          sortKey="status"
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                          onSort={handleSortColumn}
                          className="min-w-[160px]"
                        >
                          Status
                        </SortableTh>
                        <th className="sticky right-0 top-0 z-10 w-10 bg-[#f7f7f7] px-4 py-2.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td
                            colSpan={11}
                            className="px-5 py-12 text-center text-sm text-[#777]"
                          >
                            Loading status table…
                          </td>
                        </tr>
                      ) : displayedRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={11}
                            className="px-5 py-12 text-center text-sm text-[#777]"
                          >
                            {items.length === 0
                              ? "No applications to show."
                              : "No rows match your search."}
                          </td>
                        </tr>
                      ) : (
                        displayedRows.map((row) => (
                          <tr
                            key={row.id}
                            className={`group cursor-pointer border-b border-[#eee] transition-colors hover:bg-[#fafafa] ${
                              navigatingId === row.id
                                ? "pointer-events-none opacity-60"
                                : ""
                            }`}
                            onClick={() => void handleRowClick(row)}
                          >
                            <td
                              className="sticky left-0 z-[1] border-r border-[#eee] bg-white px-4 py-3 group-hover:bg-[#fafafa]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                onClick={(e) => toggleSelectRow(e, row.id)}
                                className="flex h-6 w-6 items-center justify-center"
                                aria-label={`Select row ${row.tmNo}`}
                              >
                                <img
                                  src={iconCheckbox}
                                  alt=""
                                  className="h-6 w-6"
                                />
                              </button>
                            </td>
                            <td className="px-4 py-3 text-xs tabular-nums text-[#222]">
                              {row.no}
                            </td>
                            <td className="px-4 py-3 text-xs text-[#222]">
                              {row.mark || "—"}
                            </td>
                            <td className="px-4 py-3 text-xs text-[#222]">
                              {row.typeOfMark || "—"}
                            </td>
                            <td className="px-4 py-3 text-xs tabular-nums text-[#222]">
                              {row.classNumber}
                            </td>
                            <td className="break-all px-4 py-3 text-xs text-[#222]">
                              {row.tmNo || "—"}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-xs text-[#222]">
                              {row.applDate || "—"}
                            </td>
                            <td className="break-all px-4 py-3 text-xs text-[#222]">
                              {row.acknowledgementNo ?? "—"}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-xs text-[#222]">
                              {row.dateOfFiling ?? "—"}
                            </td>
                            <td className="px-4 py-3">
                              {row.status?.trim() ? (
                                row.changed ? (
                                  <span className="inline-flex items-center rounded-md bg-[#fce8e8] px-2 py-0.5 text-xs font-semibold leading-5 text-[#f25116]">
                                    {row.status}
                                  </span>
                                ) : (
                                  <span className="text-xs font-normal leading-5 text-[#222]">
                                    {row.status}
                                  </span>
                                )
                              ) : (
                                <span className="text-xs font-normal leading-5 text-[#222]">
                                  —
                                </span>
                              )}
                            </td>
                            <td
                              className="sticky right-0 z-[1] bg-white px-4 py-3 group-hover:bg-[#fafafa]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="ml-auto flex h-4 w-4 items-center justify-center rounded hover:bg-[#f7f7f7]"
                                aria-label="More options"
                              >
                                <img
                                  src={iconMore}
                                  alt=""
                                  className="h-4 w-4"
                                />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex w-full flex-col flex-wrap gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-0.5 text-xs text-[#6d6d6d]">
                  <span className="font-normal leading-5">
                    {totalRecords === 0
                      ? "No records"
                      : `Showing ${(page - 1) * PAGE_SIZE + 1} to ${Math.min(
                          page * PAGE_SIZE,
                          totalRecords
                        )} of ${totalRecords} records`}
                  </span>
                  {totalRecords > 0 && (
                    <span className="leading-4 text-[#90a1b9]">
                      Page {page} of {totalPages}
                    </span>
                  )}
                </div>
                <nav
                  className="flex flex-row flex-wrap items-center gap-1.5"
                  aria-label="Table pagination"
                >
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage(1)}
                    className="min-h-8 rounded-md border border-[#eee] bg-white px-2.5 py-1.5 text-xs font-medium text-[#222] transition-colors hover:bg-[#f7f7f7] disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="First page"
                  >
                    First
                  </button>
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-[#eee] bg-white transition-colors hover:bg-[#f7f7f7] disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Previous page"
                  >
                    ‹
                  </button>
                  {pageList.map((entry, idx) =>
                    entry === "gap" ? (
                      <span
                        key={`gap-${idx}`}
                        className="select-none px-1 text-xs text-[#90a1b9]"
                        aria-hidden
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={entry}
                        type="button"
                        onClick={() => setPage(entry)}
                        className={`h-8 min-w-8 rounded-md border px-2 text-xs font-medium transition-colors ${
                          page === entry
                            ? "border-[#222] bg-[#222] text-white"
                            : "border-[#eee] bg-white text-[#222] hover:bg-[#f7f7f7]"
                        }`}
                        aria-label={`Page ${entry}`}
                        aria-current={page === entry ? "page" : undefined}
                      >
                        {entry}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-[#eee] bg-white transition-colors hover:bg-[#f7f7f7] disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Next page"
                  >
                    ›
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage(totalPages)}
                    className="min-h-8 rounded-md border border-[#eee] bg-white px-2.5 py-1.5 text-xs font-medium text-[#222] transition-colors hover:bg-[#f7f7f7] disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Last page"
                  >
                    Last
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </main>
      <UploadStatusModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUploadComplete={() => void fetchApplications()}
      />
    </DashboardLayout>
  );
};
